import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogOut, LayoutDashboard, Users, FileEdit, Building2, Menu, X, BookOpen, Heart, MessageSquare, Shield, Fingerprint, CalendarDays, FileText, GraduationCap, Briefcase, Sparkles, ScrollText, ListChecks, Activity } from 'lucide-react';
import { useState } from 'react';
import NotificationBell from '@/components/NotificationBell';
import GlobalSearch from '@/components/GlobalSearch';
import IssueReporter from '@/components/IssueReporter';
import { canAccessClientIntel, isScopeRestricted, isHrHomeUser } from '@/lib/permissions';

// Map email → personalised cheat-sheet PDF slug. One slug per leader role.
// Updated 27-Apr-2026 — see /app/scripts/generate_cheatsheets.py for source.
const CHEATSHEET_BY_EMAIL = {
  'dipali@clubconcierge.in':         'dipali_ceo',
  'nihal@mindescapes.in':            'nihal_me',         // primary slug; TDB sheet linked from inside the ME sheet
  'dhiraj@mindescapes.in':           'dhiraj',
  'roji.mathew@mindescapes.in':      'roji',
  'nisha@clubconcierge.in':          'nisha',
  'samar@clubconcierge.in':          'samar',
  'srikanth.ds@lesconcierges.co.in': 'srikanth',
  'sikand.adi@gmail.com':            'roji',             // Aditya — TDB submitter, reuse Roji's
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isCeo = user?.role === 'ceo';
  const isSubmitter = user?.role === 'submitter';
  const isSiteManager = user?.role === 'site-manager';
  const isSubLeader = user?.role === 'sub_leader';
  const isCostController = user?.role === 'cost_controller';
  const isAccommodationScope = isSubLeader && user?.sub_view?.has_bookings && user?.sub_view?.scope_value?.length === 1 && user?.sub_view?.scope_value?.[0]?.includes('Stays');
  const isNilgirisScope = isSubLeader && user?.sub_view?.includes_accommodation;
  const subLabel = user?.site || user?.sub_view?.scope_value?.[0] || 'My View';
  const cheatSlug = CHEATSHEET_BY_EMAIL[user?.email];
  const cheatItem = cheatSlug
    ? { label: 'Cheat Sheet', path: `/cheatsheets/${cheatSlug}_cheatsheet.pdf`, icon: ScrollText, external: true }
    : null;

  const baseNavItems = isCeo
    ? [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Mira', path: '/mira', icon: MessageSquare },
        { label: 'Sales', path: '/sales-group', icon: Briefcase },
        { label: 'Group MIS', path: '/group-financials', icon: Briefcase },
        { label: 'Outlet Intel', path: '/outlet-intelligence', icon: Activity },
        { label: 'Client Intel', path: '/client-intelligence', icon: Sparkles },
        { label: 'Workbench', path: '/member-care/workbench', icon: ListChecks },
        { label: 'Pulse', path: '/programme-pulse', icon: Activity },
        { label: 'Values', path: '/values', icon: Shield },
        { label: 'IP Register', path: '/ip-register', icon: Fingerprint },
        { label: 'Admin', path: '/admin', icon: Users },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ]
    : isCostController
    ? [
        { label: 'My Division', path: `/division/${user?.division_id}`, icon: Building2 },
        { label: 'Group MIS', path: '/group-financials', icon: Briefcase },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ]
    : isAccommodationScope
    ? [
        { label: 'Bookings', path: '/bookings', icon: CalendarDays },
        { label: 'My Division', path: `/division/${user?.division_id}`, icon: Building2 },
        { label: 'My Training', path: '/my-training', icon: GraduationCap },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ]
    : isNilgirisScope
    ? [
        { label: subLabel, path: `/division/${user?.division_id}`, icon: Building2 },
        { label: 'Bookings', path: '/bookings', icon: CalendarDays },
        { label: 'Submit Update', path: `/update/${user?.division_id}`, icon: FileEdit },
        { label: 'My Training', path: '/my-training', icon: GraduationCap },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ]
    : (isSubmitter)
    ? [
        { label: 'My Division', path: `/division/${user?.division_id}`, icon: Building2 },
        { label: 'Submit Update', path: `/update/${user?.division_id}`, icon: FileEdit },
        { label: 'My Training', path: '/my-training', icon: GraduationCap },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ]
    : (isSiteManager || isSubLeader)
    ? [
        { label: subLabel, path: `/division/${user?.division_id}`, icon: Building2 },
        { label: 'Group MIS', path: '/group-financials', icon: Briefcase },
        { label: 'Submit Update', path: `/update/${user?.division_id}`, icon: FileEdit },
        { label: 'My Training', path: '/my-training', icon: GraduationCap },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ]
    : [
        { label: 'My Division', path: `/division/${user?.division_id}`, icon: Building2 },
        { label: 'Group MIS', path: '/group-financials', icon: Briefcase },
        { label: 'Mira', path: '/mira', icon: MessageSquare },
        { label: 'Submit Update', path: `/update/${user?.division_id}`, icon: FileEdit },
        { label: 'My Training', path: '/my-training', icon: GraduationCap },
        { label: 'Values', path: '/values', icon: Shield },
        { label: 'My Mirror', path: '/mirror', icon: Heart },
        { label: 'Our Story', path: '/our-story', icon: BookOpen },
        { label: 'Docs', path: '/docs', icon: FileText },
      ];

  // Append the personalised "Cheat Sheet" PDF link to the END of every
  // role's nav so leaders can always reach the 1-pager that explains what to
  // do on WhatsApp / Mira. Skipped silently for users without a mapping.
  const navItems = cheatItem ? [...baseNavItems, cheatItem] : baseNavItems;

  // Concierge® team (Nisha, Samar, Shiva, Tanisha) need Client Intel
  // alongside their division view — same place CEO finds it. CEO already
  // has the link in baseNavItems above so we skip duplicating it.
  // Doctrine: 30-Apr-2026 — Dipali confirmed B2C team needs CI access.
  // 09-May-2026 — same team also needs Workbench access (the LSM team
  // actually runs Member Care; they were missing the nav link entirely).
  if (!isCeo && canAccessClientIntel(user)) {
    const wbItem = { label: 'Workbench', path: '/member-care/workbench', icon: ListChecks };
    const ciItem = { label: 'Client Intel', path: '/client-intelligence', icon: Sparkles };
    const pulseItem = { label: 'Pulse', path: '/programme-pulse', icon: Activity };
    // Insert Workbench + Client Intel just before the cheat sheet
    // (last item) if present, else append. Keep order: Workbench first
    // (where they triage), Client Intel second (where they dive deeper).
    if (cheatItem) {
      navItems.splice(navItems.length - 1, 0, wbItem, ciItem, pulseItem);
    } else {
      navItems.push(wbItem, ciItem, pulseItem);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Scope-restricted users (Rekha · HR, future role-restricted accounts).
  // 11-May-2026: if `user.scopes` is non-empty, treat it as a tight
  // whitelist. Only nav items mapped to one of the user's scopes survive.
  // Path-prefix matching keeps this maintainable when paths change.
  // ─────────────────────────────────────────────────────────────────────
  const NAV_PATH_SCOPE_MAP = [
    // [pathPrefix, scopeToken]
    ['/people',                'people'],
    ['/my-training',           'training'],
    ['/training',              'training'],
    ['/profile',               'profile'],
    ['/update/',               'submit_update'],
    ['/division/',             'division'],
    ['/outlet-intelligence',   'outlets'],
    ['/client-intelligence',   'client_intel'],
    ['/member-care',           'workbench'],
    ['/mira',                  'mira'],
    ['/sales-group',           'finance'],
    ['/group-financials',      'finance'],
    ['/dashboard',             'dashboard'],
    ['/admin',                 'admin'],
    ['/ip-register',           'ip_register'],
    ['/values',                'values'],
    ['/mirror',                'mirror'],
    ['/our-story',             'our_story'],     // always allowed for everyone
    ['/docs',                  'docs'],          // always allowed for everyone
    ['/cheatsheets',           'cheatsheet'],    // personal — always allowed
    ['/bookings',              'bookings'],
  ];
  if (isScopeRestricted(user)) {
    // "our_story", "docs", "cheatsheet" are never hidden — they are
    // org-wide reading material safe for any role.
    const ALWAYS_ALLOWED = new Set(['our_story', 'docs', 'cheatsheet']);
    const allowed = new Set([...(user.scopes || []), ...ALWAYS_ALLOWED]);

    // First, INJECT nav items for scopes the user has but the role's
    // default nav doesn't include (e.g. People — only CEO had it; Rekha
    // needs it). Idempotent: skip if path already in navItems.
    const SCOPE_INJECTABLES = [
      { scope: 'people',         label: 'HR Hub',         path: '/people',                icon: Users },
      { scope: 'training',       label: 'My Training',    path: '/my-training',           icon: GraduationCap },
      { scope: 'profile',        label: 'My Profile',     path: `/profile/${user?.email}`, icon: Heart },
      { scope: 'submit_update',  label: 'Submit Update',  path: `/update/${user?.division_id}`, icon: FileEdit },
      { scope: 'outlets',        label: 'Outlet Intel',   path: '/outlet-intelligence',   icon: Activity },
      { scope: 'client_intel',   label: 'Client Intel',   path: '/client-intelligence',   icon: Sparkles },
      { scope: 'workbench',      label: 'Workbench',      path: '/member-care/workbench', icon: ListChecks },
    ];
    const injected = [];
    SCOPE_INJECTABLES.forEach(({ scope, label, path, icon }) => {
      if (!allowed.has(scope)) return;
      if (navItems.some((n) => n.path === path)) return;
      injected.push({ label, path, icon });
    });
    navItems.unshift(...injected);

    const filtered = navItems.filter((item) => {
      const match = NAV_PATH_SCOPE_MAP.find(([prefix]) => (item.path || '').startsWith(prefix));
      if (!match) return false; // path not mapped → assume restricted, hide
      return allowed.has(match[1]);
    });
    // Replace navItems IN PLACE so the JSX below renders the filtered list.
    navItems.length = 0;
    filtered.forEach((it) => navItems.push(it));
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F5F2' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: '#E5E1DC' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={isCeo ? '/dashboard' : (isHrHomeUser(user) ? '/people' : `/division/${user?.division_id}`)} className="flex items-center gap-2">
              <span className="font-bold text-sm sm:text-base tracking-tight" style={{ color: '#1B2A4A' }} data-testid="header-title">
                LCG Dashboard
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden sm:flex items-center gap-1 ml-4">
              {navItems.map(item => {
                const linkProps = item.external
                  ? { to: item.path, target: '_blank', rel: 'noopener noreferrer' }
                  : { to: item.path };
                return (
                  <Link
                    key={item.path}
                    {...linkProps}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      location.pathname === item.path ? 'text-white' : ''
                    }`}
                    style={location.pathname === item.path ? { backgroundColor: '#1B2A4A', color: 'white' } : { color: '#6B7280' }}
                  >
                    <item.icon size={15} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <GlobalSearch />
            <NotificationBell />
            <span className="hidden sm:block text-xs" style={{ color: '#6B7280' }} data-testid="header-user-name">
              {user?.name}
            </span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs hover:bg-gray-100 transition-colors"
              style={{ color: '#6B7280' }}
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>

            {/* Mobile menu toggle */}
            <button
              className="sm:hidden p-1"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="sm:hidden border-t px-4 py-2 space-y-1" style={{ borderColor: '#E5E1DC' }}>
            {navItems.map(item => {
              const linkProps = item.external
                ? { to: item.path, target: '_blank', rel: 'noopener noreferrer' }
                : { to: item.path };
              return (
                <Link
                  key={item.path}
                  {...linkProps}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === item.path ? 'text-white' : ''
                  }`}
                  style={location.pathname === item.path ? { backgroundColor: '#1B2A4A', color: 'white' } : { color: '#6B7280' }}
                >
                  <item.icon size={16} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>

      {/* Global floating issue reporter — visible on every page */}
      <IssueReporter />
    </div>
  );
}
